import { MapTemplateDatasetExtended } from "@biggeo/bg-server-lib/studio";
import { EmptyState } from "@biggeo/bg-ui/lab";
import { TableRowsOutline } from "@biggeo/bg-ui/lab/icons";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/lib/Option";
import { pipe } from "fp-ts/lib/function";
import isEmpty from "lodash/isEmpty";
import React, { useState } from "react";
import { useNavigate } from "react-router";

import { DatasetPointShape } from "../../components/DatapointShape/types";
import { Routes } from "../../navigation/redux/model";
import { DEFAULT_LOGIC_OPERATOR } from "../filter-criteria/utils/utils";
import {
    SetDatasetContextType,
    getDefaultDatasetConfiguration,
} from "../mapbox/context/utils/general-utils";
import { MapLayoutLeftContent } from "../views/MapLayoutLeftContent";
import { AddDatasetSubMenuContainer } from "../../data/containers/AddDatasetSubMenuContainer";
import { useMap } from "../mapbox/context";

type MapLayoutLeftContentContainerProps = React.PropsWithChildren<
    {
        readonly map: React.MutableRefObject<mapboxgl.Map | null>;
        readonly mapTemplateId: string;
        readonly datasetShape?: readonly DatasetPointShape[];
        readonly setDatasetShape?: (datasetShape: DatasetPointShape) => void;
        readonly isGlobalTemplate?: boolean;
        readonly isMapLoaded: boolean;
        readonly isSavedViewPage: boolean;
    } & SetDatasetContextType
>;

export const MapLayoutLeftContentContainer = ({
    mapTemplateId,
    isGlobalTemplate,
    map,
    isMapLoaded,
    addDatasets,
    updateDataset,
    isSavedViewPage,
    reorderDatasets,
}: MapLayoutLeftContentContainerProps) => {
    const toPage = useNavigate();

    const [localDataList, setLocalDataList] = useState<string[]>([]);

    const { datasets } = useMap();

    const setUpdatedDatasets = async (
        datasetsToAdd: readonly MapTemplateDatasetExtended[]
    ) => {
        addDatasets(
            datasetsToAdd.map((d) => ({
                dataSource: d.dataSource,
                mapTemplateDataset: d.mapTemplateDataset,
                dataSourceConfiguration: d.dataSourceConfiguration,
                isVisible: false,
                isSelected: false,
                isGettingStyled: false,
                isTableViewed: false,
                isLegendOpen: false,
                configuration: getDefaultDatasetConfiguration(),
                filters: {
                    logicOperator: DEFAULT_LOGIC_OPERATOR,
                    filters: [],
                },
                selectedRows: [],
                pinnedRows: [],
                isSample: false,
                fkMarketplaceDatasetId: undefined,
                organization: undefined,
                image: undefined,
            }))
        );
    };

    const toDataManagementPage = () => {
        toPage(`${Routes.data}/map-template/${mapTemplateId}`);
    };

    return pipe(
        datasets,
        O.fromPredicate((datasets) => !isEmpty(datasets)),
        O.fold(
            () => (
                <EmptyState
                    icon={<TableRowsOutline />}
                    title="No Datasets Enabled"
                    subtitle="Enable datasets to use on this map"
                    border={false}
                    onClick={toDataManagementPage}
                    buttonWithMenu={
                        <AddDatasetSubMenuContainer
                            mapTemplateId={mapTemplateId}
                            existingDatasets={pipe(
                                datasets,
                                A.map((d) => d.dataSource)
                            )}
                            setRemote={setUpdatedDatasets}
                            localDataList={localDataList}
                            setLocalDataList={setLocalDataList}
                        />
                    }
                />
            ),
            (data) => (
                <MapLayoutLeftContent
                    datasets={data}
                    map={map}
                    isMapLoaded={isMapLoaded}
                    mapTemplateId={mapTemplateId}
                    isGlobalTemplate={isGlobalTemplate}
                    setRemote={setUpdatedDatasets}
                    localDataList={localDataList}
                    setLocalDataList={setLocalDataList}
                    updateDataset={updateDataset}
                    isSavedViewPage={isSavedViewPage}
                    reorderDatasets={reorderDatasets}
                />
            )
        )
    );
};
